import { usePlayer } from "@/context/PlayerContext";
import { fileUrl } from "@/lib/api";
import { Play, Pause, SkipBack, SkipForward, Radio as RadioIcon } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import ExplicitBadge from "@/components/ExplicitBadge";

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60), s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Stable pseudo-random waveform per current track (visual only)
function useWave(seed, bars = 48) {
  return useMemo(() => {
    let h = 0;
    for (let i = 0; i < (seed || "x").length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
    const rnd = () => { h = (h * 9301 + 49297) % 233280; return h / 233280; };
    return Array.from({ length: bars }, () => 0.25 + rnd() * 0.75);
  }, [seed, bars]);
}

export default function PersistentPlayer() {
  const { current, playing, progress, duration, access, mode, toggle, next, prev, seek, PREVIEW_SEC } = usePlayer();
  const wave = useWave(current?.id || "");
  if (!current) return null;
  // In radio mode we're playing full songs (marketed as "Free radio"); the
  // 30-second preview cap only applies to non-radio playback of paid tracks.
  const isPreview = mode !== "radio" && !access.full;
  const capDuration = isPreview ? Math.min(duration || PREVIEW_SEC, PREVIEW_SEC) : duration;
  const pct = capDuration ? progress / capDuration : 0;
  const activeIdx = Math.floor(pct * wave.length);

  const clickWave = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const p = (e.clientX - rect.left) / rect.width;
    seek(p * duration);
  };

  return (
    <div data-testid="persistent-player" className="fixed bottom-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-t border-white/10">
      <div className="max-w-[1400px] mx-auto grid grid-cols-[auto_1fr_auto] items-center gap-3 md:gap-6 px-3 md:px-6 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-12 w-12 bg-neutral-800 flex-shrink-0 overflow-hidden">
            {current.cover_path
              ? <img src={fileUrl(current.cover_path)} alt="" className="h-full w-full object-cover" />
              : <div className="h-full w-full grid place-items-center text-[#FF4500]"><RadioIcon className="h-6 w-6" /></div>}
          </div>
          {/* Tapping the title/artist deep-links to the artist page — accidental
              taps become free exposure for the artist. */}
          <Link
            to={current.artist_id ? `/artist/${current.artist_id}` : "#"}
            data-testid="player-artist-link"
            className="min-w-0 hidden sm:block max-w-[220px] group"
          >
            <div data-testid="player-title" className="font-display text-sm truncate group-hover:text-[#FF4500] transition-colors flex items-center gap-2">
              <span className="truncate">{current.title}</span>
              <ExplicitBadge explicit={current.is_explicit} trackId={current.id} size="xs" />
            </div>
            <div data-testid="player-artist" className="font-mono text-[10px] text-gray-400 truncate uppercase tracking-widest">
              {current.artist_name} · {mode === "radio" ? <span className="text-[#FF4500]">on air</span> : isPreview ? <span className="text-[#FF4500]">preview 0:30</span> : "on air"}
            </div>
          </Link>
        </div>

        {/* Waveform + controls */}
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <button data-testid="player-prev" onClick={prev} className="p-2 hover:text-[#FF4500] transition-colors shrink-0"><SkipBack className="h-5 w-5" /></button>
          <button data-testid="player-toggle" onClick={toggle} className="h-10 w-10 grid place-items-center bg-[#FF4500] text-black hover:bg-[#E53E00] transition-colors shrink-0">
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </button>
          <button data-testid="player-next" onClick={next} className="p-2 hover:text-[#FF4500] transition-colors shrink-0"><SkipForward className="h-5 w-5" /></button>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="font-mono text-[10px] text-gray-500 tabular-nums w-10 text-right shrink-0">{fmt(progress)}</span>
            <div data-testid="player-wave" onClick={clickWave}
                 className="flex-1 min-w-0 h-8 flex items-end gap-[2px] cursor-pointer">
              {wave.map((h, i) => (
                <div key={`${current.id}-bar-${i}`} className={`flex-1 min-w-[2px] transition-colors ${i <= activeIdx ? "bg-[#FF4500]" : "bg-white/15"}`}
                     style={{ height: `${Math.max(8, h * 100)}%` }} />
              ))}
            </div>
            <span className="font-mono text-[10px] text-gray-500 tabular-nums w-10 shrink-0">{fmt(duration)}</span>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500">
          <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF4500] opacity-75" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#FF4500]" /></span>
          Trending Radio
        </div>
      </div>
    </div>
  );
}
